import { useRef, useState } from "react";
import cx from "classnames";

import { useIntersectionObserver } from "common/utils/dom";
import Image from "common/components/Image";

import Container from "client/common/components/layouts/Container";
import { useClinic, useFeatures } from "client/clinics/hooks";

import styles from "../styles/PreFooter.module.less";

const Hearts = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useIntersectionObserver(containerRef, () => setTimeout(() => setIsVisible(true), 1000), {
    threshold: 1,
  });

  return (
    <div ref={containerRef} className={cx(styles.hearts, isVisible && styles.visible)}>
      <svg
        width="43"
        height="40"
        viewBox="0 0 43 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M30.0654 8.11773C28.3663 6.91525 25.8486 6.26591 23.5611 6.89986C21.857 7.37219 21.2536 8.61796 21.2646 10.0205C21.2696 10.6391 21.3847 11.2423 21.5488 11.8319C21.6659 12.2475 21.7399 12.7814 22.0251 13.1354C22.5324 13.7665 23.6712 13.8858 24.4977 14.058C25.6405 14.2956 26.7432 14.6005 27.84 14.9392C28.7235 15.2133 29.7052 15.5529 30.3636 16.1224C31.8216 17.3816 32.6822 18.9987 33.7208 20.5129C33.7849 20.6072 34.4313 21.5865 34.4893 21.4345C34.5674 21.2344 34.5184 20.9968 34.5294 20.7947C34.5624 20.1618 34.6324 19.523 34.7235 18.89C35.0297 16.7679 35.7122 14.6823 36.1775 12.5717C36.5307 10.9652 36.7588 9.3481 37.044 7.73678C37.3332 6.09852 37.7705 4.15724 36.8789 2.66424C36.3686 1.81 34.7875 1.84271 33.7859 2.11688C33.2145 2.27272 32.1448 2.40739 31.7786 2.88935C31.4914 3.26837 31.1682 3.53292 31.0311 3.97735C30.915 4.35541 30.871 4.67768 30.865 5.06632C30.8539 5.85899 31.0871 6.63627 31.1511 7.4174C31.1742 7.69253 31.2142 7.99459 31.1581 8.27068C31.1391 8.36496 31.0851 8.87866 31.01 8.63431M9.29569 27.7162C8.87742 26.3464 7.81772 25.0304 6.37777 24.6061C5.30406 24.2887 4.50453 24.8562 3.96317 25.7432C3.7305 26.1362 3.54358 26.5527 3.4058 26.9851C3.30373 27.2891 3.13562 27.6518 3.14563 27.9769C3.16464 28.558 3.712 29.0428 4.07625 29.4487C4.57858 30.0105 5.03588 30.5993 5.47517 31.2073C5.8294 31.6979 6.20865 32.2664 6.33074 32.8619C6.60091 34.1798 6.41979 35.5073 6.37076 36.8349C6.36776 36.9176 6.32273 37.767 6.41279 37.692C6.53087 37.5939 6.59791 37.4265 6.68297 37.3033C6.94614 36.9176 7.23233 36.5405 7.52652 36.1759C8.51318 34.9494 9.68195 33.8825 10.7467 32.7224C11.5572 31.8374 12.3077 30.9013 13.0832 29.9903C13.8727 29.063 14.8574 27.999 14.9744 26.7379C15.0405 26.0164 14.2039 25.4681 13.5735 25.2785C13.2153 25.1708 12.6049 24.8707 12.2266 25.0419C11.9284 25.1766 11.6563 25.2276 11.4121 25.4575C11.2144 25.6437 11.0428 25.8539 10.9018 26.0828C10.5865 26.5782 10.4054 27.1506 10.1342 27.6662C10.0392 27.848 9.94212 28.0519 9.80603 28.2049C9.759 28.2588 9.53085 28.5618 9.58589 28.3819L9.29569 27.7162Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          pathLength={1}
          className={styles.path}
        />
      </svg>
    </div>
  );
};

const Images = () => {
  const clinic = useClinic();
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useIntersectionObserver(containerRef, () => setTimeout(() => setIsVisible(true), 500), {
    threshold: 1,
  });

  return (
    <div ref={containerRef} className={cx(styles.images, isVisible && styles.visible)}>
      {clinic.clinic_user_images.map((image) => (
        <div key={image.url} className={styles.imageContainer}>
          <Image className={styles.image} src={image.url} alt="Staff" />
        </div>
      ))}
    </div>
  );
};

type PreFooterProps = {
  image: string;
  className?: string;
};

const PreFooter = ({ image, className }: PreFooterProps) => {
  const clinic = useClinic();
  const features = useFeatures();

  return (
    <div className={cx(styles.container, className)} style={{ backgroundImage: `url(${image})` }}>
      <Container>
        <div className={styles.content}>
          <div className={styles.thankYou}>
            <p className={styles.heading}>
              Thank you for visiting our online store and supporting us before shopping
              big-box-retailers :)
            </p>
            {features.CLINIC_USER_PROFILE_IMAGES && <Images />}
            <div className={styles.team}>
              <div className={styles.text}>
                {clinic.display_name}{" "}
                <span className={styles.teamHearts}>
                  Team <Hearts />
                </span>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PreFooter;
